import {observable, runInAction} from "mobx";
import dashboardStore from './ProfileInfoStore';
import successStore from "./SuccessStore";
import {IntegrationIntervalDto} from "../Entities/Dtos";
import api from "../Services/Api";
import profileInfoStore from "./ProfileInfoStore";
import integrationIntervalsLookupStore from "./IntegrationIntervalsLookupStore";
import utils from "../Services/Utils";

interface ISkladStore {
    load: () => Promise<void>;
    updateUserData: () => Promise<void>;
    save: () => Promise<void>;
    tryRunJob: () => void;
    runJob: () => Promise<void>;
    stopJob: () => Promise<void>;
    jobIsStopping: boolean;
    dataUpdateIsRunning: boolean;

    intervalId: string;
    selectedInterval: IntegrationIntervalDto;

    integrationField?: string;
    bundleSalePriceId?: string;
    notCalculateBundlePriceIfFilled: boolean;
    enableBundlePriceCalculation: boolean;
    customStockFieldId?: string;
    enableCustomStock: boolean;

    needCheckStores: boolean;
    onlyBundles: boolean;
    needCheckStock: boolean;
    removeTransit: boolean;
    addTransit: boolean;
    removeReserve: boolean;

    fastStockCheck: boolean;
    checkRealStock: boolean;
    checkFreeStock: boolean;
    checkQuantity: boolean;

    ignoreIntegrationFieldInComponents: boolean;
    allowProductDuplications: boolean;

    isLoaded: boolean;
    loading: boolean;
    period: string;
    isSaving: boolean;
    hasChanges: boolean;

    showPriceInformation: boolean;
    trimLastWordInAdTitleIfLong: boolean;
    useMaths: boolean;

    minStockQuantity: number;
}

const skladStore = observable<ISkladStore>({
    onlyBundles: false,
    useMaths: false,
    showPriceInformation: false,
    jobIsStopping: false,
    dataUpdateIsRunning: false,
    hasChanges: false,
    period: '06:15',
    isLoaded: false,
    needCheckStores: false,
    needCheckStock: false,
    removeTransit: false,
    addTransit: false,
    removeReserve: false,
    trimLastWordInAdTitleIfLong: false,

    fastStockCheck: false,
    checkRealStock: false,
    checkFreeStock: false,
    checkQuantity: false,

    ignoreIntegrationFieldInComponents: false,
    allowProductDuplications: false,
    enableCustomStock: false,
    notCalculateBundlePriceIfFilled: false,
    enableBundlePriceCalculation: false,
    isSaving: false,
    loading: false,
    minStockQuantity: 1,
    intervalId: '',
    async load() {
        await integrationIntervalsLookupStore.load();
        this.loading = true;
        const result = await api.get('settings');
        runInAction(() => {
            if (result.success) {
                this.useMaths = result.data.useMaths;

                this.intervalId = result.data.intervalId;

                this.integrationField = result.data.integrationField;
                this.bundleSalePriceId = result.data.bundleSalePriceId;
                this.customStockFieldId = result.data.customStockFieldId;
                this.notCalculateBundlePriceIfFilled = result.data.notCalculateBundlePriceIfFilled;

                this.enableBundlePriceCalculation = this.bundleSalePriceId !== undefined;
                this.enableCustomStock = this.customStockFieldId !== undefined;

                this.needCheckStores = result.data.needCheckStores;
                this.needCheckStock = result.data.needCheckStock;
                this.removeTransit = result.data.removeTransit;
                this.addTransit = result.data.addTransit;
                this.removeReserve = result.data.removeReserve;

                this.fastStockCheck = result.data.fastStockCheck;
                this.checkRealStock = result.data.checkRealStock;
                this.checkFreeStock = result.data.checkFreeStock;
                this.checkQuantity = result.data.checkQuantity;

                this.ignoreIntegrationFieldInComponents = result.data.ignoreIntegrationFieldInComponents;
                this.allowProductDuplications = result.data.allowProductDuplications;

                this.period = result.data.period;
                this.minStockQuantity = result.data.minStockQuantity;
                
                this.hasChanges = false;

                this.jobIsStopping = false;
                this.onlyBundles = result.data.onlyBundles;
                this.trimLastWordInAdTitleIfLong = result.data.trimLastWordInAdTitleIfLong;
            }
            this.isLoaded = true;
            this.loading = false;
        })
    },
    async updateUserData() {
        if (this.dataUpdateIsRunning) return;

        this.dataUpdateIsRunning = true;
        await api.post('profile/updateUserData', {});
    },
    async save() {
        if (this.isSaving) return;
        if (!this.enableBundlePriceCalculation && this.bundleSalePriceId !== null || undefined) {
            this.bundleSalePriceId = undefined;
        }
        if (!this.enableCustomStock && this.customStockFieldId !== null || undefined) {
            this.customStockFieldId = undefined;
        }
        this.isSaving = true;
        const result = await api.post('settings/save', {
            integrationField: utils.toNullIfEmpty(this.integrationField),
            bundleSalePriceId: utils.toNullIfEmpty(this.bundleSalePriceId),
            customStockFieldId: utils.toNullIfEmpty(this.customStockFieldId),
            notCalculateBundlePriceIfFilled: this.notCalculateBundlePriceIfFilled,
            needCheckStores: this.needCheckStores,
            needCheckStock: this.needCheckStock,
            removeTransit: this.removeTransit,

            fastStockCheck: this.fastStockCheck,
            checkRealStock: this.checkRealStock,
            checkFreeStock: this.checkFreeStock,
            checkQuantity: this.checkQuantity,

            ignoreIntegrationFieldInComponents: this.ignoreIntegrationFieldInComponents,
            allowProductDuplications: this.allowProductDuplications,
            addTransit: this.addTransit,
            removeReserve: this.removeReserve,
            period: this.period,
            intervalId: this.intervalId,
            onlyBundles: this.onlyBundles,
            trimLastWordInAdTitleIfLong: this.trimLastWordInAdTitleIfLong,
            useMaths: this.useMaths,
            
            minStockQuantity: this.minStockQuantity
        });

        if (result.success) {
            await dashboardStore.load();
            await this.load();
        }
        this.isSaving = false;
    },
    async runJob() {
        if (this.isSaving) return;
        this.jobIsStopping = true;

        const result = await api.post('profile/runJob', {});
        if (result.success) {
            successStore.add("Выгрузка успешно запущена");
            await dashboardStore.load();
            await this.load();
        }
    },
    async stopJob() {
        this.jobIsStopping = true;
        const result = await api.post('profile/stopJob', {});
        if (result.success) {
            await dashboardStore.load();
            await this.load();
        }
    },
    get selectedInterval(): IntegrationIntervalDto {
        return integrationIntervalsLookupStore.intervals.filter((interval) => interval.id === this.intervalId)[0];
    },
    tryRunJob() {
        if (profileInfoStore.jobIsRunning) {
            void this.stopJob()
        } else {
            void this.runJob();
        }
    }
});

export default skladStore;
